"use client";

import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Stat } from "@/components/ui/ApplePrimitives";
import {
  Banknote,
  TrendingUp,
  Users,
  Percent,
  Info,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { adminApi } from "@/lib/adminApi";

interface RefundRatePoint {
  month: string;
  completedCount: number;
  refundedCount: number;
  refundRatePct: number;
}

interface FinanceStats {
  revenue: {
    todayUsdCents: number;
    weekUsdCents: number;
    monthUsdCents: number;
    allTimeUsdCents: number;
  };
  arpu: {
    usdCents: number;
    payingUsers: number;
    totalUsers: number;
  };
  freeToPaidConversion: number;
  refundRateTrend: RefundRatePoint[];
  mrrUsdCents: number;
  arrUsdCents: number;
  churnRatePct: number | null;
  notes: string;
  isSubscriptionBased: boolean;
}

function formatUsd(cents: number): string {
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const tooltipStyle = {
  backgroundColor: "#18181b",
  border: "1px solid #27272a",
  borderRadius: "0.5rem",
  color: "#fafafa",
};

export default function FinancePage() {
  const [stats, setStats] = useState<FinanceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchFinance() {
      try {
        const data = await adminApi.post<{ success: boolean; stats: FinanceStats }>(
          "/admin/stats/finance",
          {},
        );
        if (data.success) setStats(data.stats);
      } catch (err) {
        console.error("Failed to fetch finance stats:", err);
        setError("Failed to load finance data");
      } finally {
        setLoading(false);
      }
    }
    fetchFinance();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">Finance</h1>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            Revenue, refunds, and key conversion metrics. All amounts in USD.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6 animate-pulse">
                <div className="h-8 w-8 rounded mb-4" />
                <div className="h-8 w-20 rounded" />
                <div className="h-4 w-16 rounded mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">Finance</h1>
          <p className="mt-1 text-sm text-red-500">{error || "Failed to load finance data"}</p>
        </div>
      </div>
    );
  }

  const totalCompletedInTrend = stats.refundRateTrend.reduce(
    (s, m) => s + m.completedCount,
    0,
  );
  const totalRefundedInTrend = stats.refundRateTrend.reduce(
    (s, m) => s + m.refundedCount,
    0,
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Finance</h1>
        <p className="text-sm">
          Revenue, refunds, and key conversion metrics. All amounts in USD.
        </p>
      </div>

      {/* Revenue KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm">Today</p>
                <p className="mt-1 text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-50">
                  {formatUsd(stats.revenue.todayUsdCents)}
                </p>
              </div>
              <Banknote className="h-8 w-8 text-merry-pink" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm">This Month</p>
                <p className="mt-1 text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-50">
                  {formatUsd(stats.revenue.monthUsdCents)}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-merry-pink" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm">ARPU</p>
                <p className="mt-1 text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-50">
                  {formatUsd(stats.arpu.usdCents)}
                </p>
                <p className="text-xs mt-1">
                  across {stats.arpu.payingUsers.toLocaleString()} paying user
                  {stats.arpu.payingUsers === 1 ? "" : "s"}
                </p>
              </div>
              <Users className="h-8 w-8 text-merry-pink" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm">Free → Paid</p>
                <p className="mt-1 text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-50">
                  {(stats.freeToPaidConversion * 100).toFixed(2)}%
                </p>
                <p className="text-xs mt-1">
                  {stats.arpu.payingUsers.toLocaleString()} of {stats.arpu.totalUsers.toLocaleString()} users
                </p>
              </div>
              <Percent className="h-8 w-8 text-merry-pink" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* N/A banner for MRR / Churn */}
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/10 p-4">
        <Info className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
            MRR / ARR / Churn: N/A
          </p>
          <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
            {stats.notes}
          </p>
        </div>
      </div>

      {/* Refund rate trend */}
      <Card>
        <CardHeader>
          <CardTitle>Refund rate (last 6 months)</CardTitle>
        </CardHeader>
        <CardContent>
          {totalCompletedInTrend + totalRefundedInTrend < 10 ? (
            <p className="text-sm">
              Insufficient data (need ≥10 completed or refunded payments). Currently{" "}
              {totalCompletedInTrend + totalRefundedInTrend}.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={stats.refundRateTrend}>
                <defs>
                  <linearGradient id="refundGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" opacity={0.3} />
                <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                <YAxis
                  stroke="#71717a"
                  fontSize={12}
                  tickFormatter={(v) => `${v}%`}
                  domain={[0, "dataMax + 2"]}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value) => [`${value}%`, "Refund rate"]}
                />
                <Area
                  type="monotone"
                  dataKey="refundRatePct"
                  stroke="#ef4444"
                  fill="url(#refundGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
          <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-zinc-500">Completed (6mo)</p>
              <p className="text-lg font-semibold">
                {totalCompletedInTrend.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-zinc-500">Refunded (6mo)</p>
              <p className="text-lg font-semibold">
                {totalRefundedInTrend.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-zinc-500">All-time Revenue</p>
              <p className="text-lg font-semibold">
                {formatUsd(stats.revenue.allTimeUsdCents)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}